import {useMemo, useState} from 'react';
import {TextFieldProps} from '@mui/material';
import {
  GridFilterModel,
  gridFilterModelSelector,
  GridRenderHeaderFilterProps,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import './FilterDateField.scss';
import ArrDatePicker from '../../../arr-date-picker/ArrDatePicker';
type FilterDateFieldProps = {
  gridRenderHeaderFilterProps: GridRenderHeaderFilterProps;
  operator?: 'contains' | 'equal' | 'startWith' | 'endWith';
  className?: string;
  dataTestId: string;
} & Omit<TextFieldProps, 'variant' | 'type'>;

const FilterDateField = ({
  gridRenderHeaderFilterProps,
  operator = 'contains',
  placeholder = '',
  className,
  dataTestId, // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ...rest
}: FilterDateFieldProps) => {
  const {colDef} = gridRenderHeaderFilterProps;
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const {inputRef} = gridRenderHeaderFilterProps;

  // Find the current filters applied to this column
  const currentFieldFilters = useMemo(
    () => filterModel.items?.filter(({field}) => field === colDef.field),
    [colDef.field, filterModel.items]
  );

  const value = currentFieldFilters[0]?.value ?? '';
  const [selectedDate, setSelectedDate] = useState(value);

  const handleTextUpdate = (value: string) => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;

    // If there's no value, delete the filter for this column
    if (!value) {
      if (currentFieldFilters[0]) {
        apiRef.current.deleteFilterItem(currentFieldFilters[0]);
      }
      return;
    }

    // Otherwise, upsert a new filter or update an existing one
    const updatedFilterModal: GridFilterModel = {
      items: [
        {
          ...(currentFieldFilters[0] || {
            field: colDef.field,
            operatorValue: operator ?? 'contains', // runtime property
          }),
          value: value,
        },
        ...filterModel.items.filter((item) => item.field !== colDef.field),
      ],
    };
    apiRef.current?.setFilterModel(updatedFilterModal);
  };

  // Get the current filter value
  const setSelectedFilterDate = (date: any) => {
    if (date == 'Invalid Date') return;
    setSelectedDate(date);
    handleTextUpdate(date);
  };

  return (
    <ArrDatePicker
      dataTestId={dataTestId}
      ref={inputRef}
      inputClass={`filter-date-field ${className}`}
      type="date-picker"
      fieldVariant="outlined"
      placeholder={placeholder}
      showToday={true}
      defaultError={false}
      onChange={setSelectedFilterDate}
      initialStartDate={selectedDate}
      shouldShowClearButton={true}
    />
  );
};

export default FilterDateField;
